import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Alert, Paper, CircularProgress, Divider } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LanguageIcon from '@mui/icons-material/Language';
import CategoryIcon from '@mui/icons-material/Category';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { submitOnboarding } from '../api/whatsapp';

const categories = [
  'Retail & E-commerce', 'Education', 'Healthcare', 'Finance & Banking',
  'Travel & Hospitality', 'Real Estate', 'Technology', 'Entertainment',
  'Food & Restaurant', 'Professional Services', 'Non-Profit', 'Other'
];

const OnboardingForm = ({ account, onComplete }) => {
  const [form, setForm] = useState({
    businessName: account?.businessName || '',
    website: account?.website || '',
    industryCategory: account?.industryCategory || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.businessName || !form.industryCategory) {
      setError('Business name and industry category are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitOnboarding(form);
      setSuccess(true);
      setTimeout(() => onComplete(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f0fdf4' }}>
        <CheckCircleIcon sx={{ fontSize: 72, color: '#22c55e', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#075e54' }}>Details Submitted!</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Admin will review your details and configure your WhatsApp number.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f0f2f5', p: 3 }}>
      <Paper sx={{ maxWidth: 560, width: '100%', borderRadius: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#075e54', p: 3, color: '#fff', textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Business Onboarding</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Tell us about your business to set up WhatsApp API
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}

          <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '16px', border: '1px solid #bbf7d0', mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhoneIcon sx={{ color: '#075e54', fontSize: 20 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Your WhatsApp Number</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#075e54' }}>{account?.whatsappBusinessNumber || 'Not set'}</Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField label="Business Name" fullWidth required
              value={form.businessName}
              onChange={e => setForm({ ...form, businessName: e.target.value })}
              placeholder="Your business name"
              sx={{ mb: 2.5 }}
              InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
              InputLabelProps={{ sx: { fontWeight: 500 } }}
            />

            <TextField label="Website URL" fullWidth
              value={form.website}
              onChange={e => setForm({ ...form, website: e.target.value })}
              placeholder="https://example.com"
              sx={{ mb: 2.5 }}
              InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
              InputLabelProps={{ sx: { fontWeight: 500 } }}
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#374151' }}>
              Industry Category
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={form.industryCategory === cat ? 'contained' : 'outlined'}
                  onClick={() => setForm({ ...form, industryCategory: cat })}
                  sx={{
                    borderRadius: '20px', textTransform: 'none', fontSize: 13,
                    bgcolor: form.industryCategory === cat ? '#075e54' : 'transparent',
                    borderColor: '#d1d5db', color: form.industryCategory === cat ? '#fff' : '#374151',
                    '&:hover': { borderColor: '#075e54', bgcolor: form.industryCategory === cat ? '#054d44' : '#f0fdf4' },
                    px: 2, py: 0.8,
                  }}
                >
                  {cat}
                </Button>
              ))}
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button type="submit" variant="contained" disabled={loading || !form.businessName || !form.industryCategory}
                sx={{
                  flex: 1, borderRadius: '14px', textTransform: 'none', py: 1.3,
                  bgcolor: '#075e54', '&:hover': { bgcolor: '#054d44' },
                  '&.Mui-disabled': { bgcolor: '#94a3b8' }
                }}>
                {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit Details'}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default OnboardingForm;
